import { createContext, useState, useContext, ReactNode, useEffect, useCallback } from 'react'

interface ShowToastParams {
  message: string
  duration?: number
  zIndex?: number
  width?: string
  toastToBeShownById?: string // should match with the id of the toast to be shown, if not provided, it will be global toast
  position?: string // should match with the position of the toast to be shown, if not provided, it will be bottom-4
  /** When set, toast stays until dismissToast (e.g. "Got it"); use with duration: 0 */
  actionLabel?: string
}

type ToastContextType = {
  message: string | null
  isVisible: boolean
  currentLocalToastId: string | null
  currentToastZIndex: number | null
  width: string | null
  position: string | null
  actionLabel: string | null
  showToast: (params: ShowToastParams) => void
  dismissToast: () => void
} | null

export const ToastContext = createContext<ToastContextType>(null)

export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [message, setMessage] = useState<string | null>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [currentLocalToastId, setCurrentLocalToastId] = useState<string | null>(null)
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null)
  const [currentToastZIndex, setCurrentToastZIndex] = useState<number | null>(null)
  const [width, setWidth] = useState<string | null>(null)
  const [position, setPosition] = useState<string | null>(null)
  const [actionLabel, setActionLabel] = useState<string | null>(null)

  const dismissToast = useCallback(() => {
    if (timeoutId) {
      clearTimeout(timeoutId)
      setTimeoutId(null)
    }
    setIsVisible(false)
    setMessage(null)
    setCurrentToastZIndex(null)
    setCurrentLocalToastId(null)
    setActionLabel(null)
    setWidth(null)
    setPosition(null)
  }, [timeoutId])

  const showToast = useCallback(
    ({
      message: newMessage,
      duration = 1500,
      zIndex,
      toastToBeShownById,
      width: newWidth,
      position: newPosition,
      actionLabel: newActionLabel,
    }: ShowToastParams) => {
      if (timeoutId) {
        clearTimeout(timeoutId)
        setTimeoutId(null)
      }

      const toastZIndex = zIndex || 1000

      setMessage(newMessage)
      setIsVisible(true)
      setCurrentToastZIndex(toastZIndex)
      setCurrentLocalToastId(toastToBeShownById || null)
      setWidth(newWidth ?? null)
      setPosition(newPosition ?? null)
      setActionLabel(newActionLabel ?? null)

      // Sticky action toasts (e.g. Got it) stay until dismissToast
      if (duration > 0 && !newActionLabel) {
        const newTimeoutId = setTimeout(() => {
          setIsVisible(false)
          setMessage(null)
          setCurrentToastZIndex(null)
          setCurrentLocalToastId(null)
          setActionLabel(null)
        }, duration)
        setTimeoutId(newTimeoutId)
      }
    },
    [timeoutId]
  )

  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [timeoutId])

  return (
    <ToastContext.Provider
      value={{
        message,
        isVisible,
        currentLocalToastId,
        currentToastZIndex,
        width,
        position,
        actionLabel,
        showToast,
        dismissToast,
      }}
    >
      {children}
    </ToastContext.Provider>
  )
}
